import { ConnectProto } from "@racoon/api";
import { isNumericId } from "@racoon/lib";
import type { LocationCriteria } from "./location-criteria";

/**
 * The first page also asks for the "status" facet, so the tab badges come with the
 * rows in one request. A facet is counted under every filter but its own, so the open
 * tab's status filter does not narrow it: one response counts both tabs.
 */
export const withStatusFacetOnFirstPage = (arg: Record<string, unknown>) => {
  const listFilter = arg.listFilter as { currentPage?: number } | undefined;
  return listFilter?.currentPage === 0
    ? { ...arg, listFilter: { ...listFilter, facets: ["status"] } }
    : arg;
};

/**
 * Every filter ListPickupLocations can carry, status aside: the table adds the open
 * tab's status, and the local mode fetches both statuses at once to count them. A
 * select value that is not an id (a stale or hand-edited URL) is dropped, not sent.
 */
export const locationListRequest = (providerId: number, criteria: LocationCriteria) => ({
  serviceProviderIds: [BigInt(providerId)],
  ...(isNumericId(criteria.typeId) && { pickupLocationTypeIds: [BigInt(criteria.typeId)] }),
  ...(isNumericId(criteria.customerId) && { serviceReceiverIds: [BigInt(criteria.customerId)] }),
  ...(isNumericId(criteria.containerTypeId) && {
    containerTypeIds: [BigInt(criteria.containerTypeId)],
  }),
  ...(isNumericId(criteria.routeGroupId) && {
    pickupLocationGroupIds: [BigInt(criteria.routeGroupId)],
  }),
  ...(criteria.search.trim() && { search: criteria.search.trim() }),
});

/**
 * The table's columns the server can order the locations by, each with the `sort_by` it
 * sends. Every one reads a container group: the server puts a location by its smallest
 * value ascending and its largest descending, locations with none last either way — the
 * same place the table's own grouped sort gives it, by its first row once sorted.
 *
 * The pickup schedule is not here, so it does not sort: its yes/no is read out of the
 * schedule JSON, and the label orders differently in each language.
 */
export const LOCATION_SORT_FIELDS: Record<string, string> = {
  containerGroup: "container_type",
  routeGroup: "route_group",
  responsibleTruck: "truck",
  customer: "customer",
  contact: "contact_name",
  contactNumber: "contact_phone",
};
/**
 * The order paged requests come back in without a sort (`id desc` on the server). An
 * unpaged request comes back oldest first, so the local mode and the export re-sort to
 * match the screen.
 */
export const newestFirst = (locations: ConnectProto.PickupLocation[]) =>
  [...locations].sort((a, b) => (a.id < b.id ? 1 : a.id > b.id ? -1 : 0));

/** The distinct providers of `locations`, plus the given ids, sorted into a stable key. */
export const providersKey = (ids: string[], locations: ConnectProto.PickupLocation[]) =>
  [
    ...new Set([
      ...ids,
      ...locations.flatMap((location) =>
        location.serviceProviderId ? [String(location.serviceProviderId)] : [],
      ),
    ]),
  ]
    .sort()
    .join(",");

/** The two statuses a tab can show; a location with any other status has no tab. */
export const TAB_STATUSES: ConnectProto.LocationStatus[] = [
  ConnectProto.LocationStatus.ACTIVE,
  ConnectProto.LocationStatus.INACTIVE,
];

/**
 * The count on each status tab, keyed by the tab's value. Paged, it is the server's
 * "status" facet; in the local mode, the locally matched set split by status. Undefined
 * until known, so no badge shows rather than a wrong 0.
 */
export const locationTabCounts = (
  source:
    | { mode: "server"; facet: Record<number, number> | undefined }
    | { mode: "local"; matched: ConnectProto.PickupLocation[] | undefined },
): Record<string, number | undefined> =>
  Object.fromEntries(
    TAB_STATUSES.map((status) => [
      status,
      source.mode === "server"
        ? source.facet?.[status]
        : source.matched?.filter((location) => Number(location.status) === status).length,
    ]),
  );
